import * as React from 'react';
import { cn } from '@/lib/utils';

const ModalSuccessHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-modal-success-header', className)}
    {...props}
  />
));
ModalSuccessHeader.displayName = 'ModalSuccessHeader';

const ModalSuccessBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-modal-success-body', 'mb-8', className)}
    {...props}
  />
));
ModalSuccessBody.displayName = 'ModalSuccessBody';

const ModalSuccessText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-modal-success-text', 'mb-2 text-base font-light', 'text-neutral-dark', className)}
    {...props}
  />
));
ModalSuccessText.displayName = 'ModalSuccessText';

const ModalSuccessName = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('c-modal-success-name', 'text-xl font-bold text-primary', 'mb-5', className)}
    {...props}
  />
));
ModalSuccessName.displayName = 'ModalSuccessName';

const ModalSuccessContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-modal-success-container', 'flex-grow basis-0', className)}
    {...props}
  />
));
ModalSuccessContainer.displayName = 'ModalSuccessContainer';

const ModalSuccessImg = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('c-modal-success-img', 'min-w-[152px] flex-grow basis-0', className)}
    {...props}
  />
));
ModalSuccessImg.displayName = 'ModalSuccessImg';

const ModalSuccessContent = React.forwardRef(({ className, isDisclaimer = false, ...props }, ref) => (
  <div
    ref={ref}
    // ESTILOS BASE
    className={cn(
      'c-modal-success-content',
      'relative my-auto flex flex-wrap items-center gap-8',
      'w-[956px] max-w-[86vw]',
      'box-border p-8 lg:p-[3.4vw]',
      'rounded-[50px] bg-white shadow-2xl',
      '-translate-y-[100px] transform opacity-0 transition-all delay-100 duration-200 ease-in-out',
      isDisclaimer && cn('c-modal-success-content-disclaimer', 'my-auto w-[800px] p-[50px]'),
      className
    )}
    {...props}
  />
));
ModalSuccessContent.displayName = 'ModalSuccessContent';

const ModalSuccess = React.forwardRef(({ className, isActive, children, ...props }, ref) => {
  const baseClasses = cn(
    'c-modal-success',
    'fixed inset-0 flex items-start justify-center',
    'bg-black/40',
    'overflow-x-hidden overflow-y-auto outline-none',
    'opacity-0 transition-opacity duration-400 ease-in-out pointer-events-none',
    'p-4',
    'z-[1060]',
    isActive && cn('pointer-events-auto opacity-100', '[&_.c-modal-success-content]:translate-y-0', '[&_.c-modal-success-content]:opacity-100')
  );

  return (
    <div
      ref={ref}
      className={cn(baseClasses, className)}
      role="dialog"
      aria-modal="true"
      tabIndex="-1"
      {...props}
    >
      {children}
    </div>
  );
});
ModalSuccess.displayName = 'ModalSuccess';

const ModalSuccessTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('c-modal-success-title', 'mb-0 text-[2.5rem] font-bold leading-tight text-primary-dark', className)}
    {...props}
  />
));
ModalSuccessTitle.displayName = 'ModalSuccessTitle';

const ModalSuccessTag = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'modal-success-tag',
      // Estilo base
      'absolute left-0 top-[60px]',
      'p-2.5',
      'rounded-br-xl rounded-tr-xl',
      'text-white',
      'bg-success',
      'text-base',
      'font-bold',
      className
    )}
    {...props}
  />
));
ModalSuccessTag.displayName = 'ModalSuccessTag';

const ModalSuccessKpiWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'modal-success-kpi-wrapper',
      // Layout
      'grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))]',
      // Sizing/Spacing
      'gap-3',
      className
    )}
    {...props}
  />
));
ModalSuccessKpiWrapper.displayName = 'ModalSuccessKpiWrapper';

const ModalSuccessKpi = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'modal-success-kpi',
      // Layout/Position
      'relative flex items-center gap-2.5',
      '[&_.c-medal]:absolute [&_.c-medal]:right-0 [&_.c-medal]:top-0 [&_.c-medal]:-translate-y-1/2 [&_.c-medal]:translate-x-1/2',
      // Sizing/Spacing
      'px-5 py-2.5',
      // Appearance
      'rounded-xl border border-solid border-primary-lightest',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
ModalSuccessKpi.displayName = 'ModalSuccessKpi';

const ModalSuccessKpiIcon = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'modal-success-kpi-icon',
      // Layout
      '[&_svg]:flex-shrink-0',
      // Sizing
      '[&_svg]:w-6',
      // Appearance
      '[&_svg]:text-primary',
      className
    )}
    {...props}
  />
));
ModalSuccessKpiIcon.displayName = 'ModalSuccessKpiIcon';

const ModalSuccessKpiInner = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'modal-success-kpi-inner',
      // Layout
      'flex-grow',
      className
    )}
    {...props}
  />
));
ModalSuccessKpiInner.displayName = 'ModalSuccessKpiInner';

const ModalSuccessKpiValue = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'modal-success-kpi-value',
      // Sizing/Spacing
      'mb-0',
      // Typography
      'text-base font-bold',
      // Appearance
      'text-primary',
      className
    )}
    {...props}
  />
));
ModalSuccessKpiValue.displayName = 'ModalSuccessKpiValue';

const ModalSuccessKpiLabel = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'modal-success-kpi-label',
      // Sizing/Spacing
      'mb-0',
      // Typography
      'text-sm font-light leading-none',
      // Appearance
      'text-[#8894AC]',
      className
    )}
    {...props}
  />
));
ModalSuccessKpiLabel.displayName = 'ModalSuccessKpiLabel';

export {
  ModalSuccessHeader,
  ModalSuccessBody,
  ModalSuccessText,
  ModalSuccessName,
  ModalSuccessContainer,
  ModalSuccessImg,
  ModalSuccessContent,
  ModalSuccess,
  ModalSuccessTitle,
  ModalSuccessTag,
  ModalSuccessKpiWrapper,
  ModalSuccessKpi,
  ModalSuccessKpiIcon,
  ModalSuccessKpiInner,
  ModalSuccessKpiValue,
  ModalSuccessKpiLabel
};
